import axios from 'axios';

export enum PositiveIdActionType {
    RxEntry = 1,
    RphVerification = 2,
    Dur = 3,
    Dispense = 4,
    Compound = 5,
    ImmunizationAdmin = 6,
    InjectableAdmin = 7,
    Transcribe = 8,
    RxAnnotate = 9,
    Enrollment = 10,
}

export enum PositiveIdTargetEntityType {
    Prescription = 1,
    Order = 2,
    CompoundBatch = 3,
    User = 4,
    Patient = 5,
}

export enum PositiveIdMethod {
    BadgeCard = 1,
    BarCode = 2,
    Biometric = 3,
    ProximityBadge = 4,
}

export interface PositiveIdChallengeRequest {
    actionType: PositiveIdActionType;
    targetEntityType: PositiveIdTargetEntityType;
    targetEntityId: string;
}

export interface PositiveIdChallengeResponse {
    challengeToken: string;
    correlationId: string;
    challengeIssuedUtc: string;
    expiresInSeconds: number;
}

export interface PositiveIdVerifyRequest {
    challengeToken: string;
    method: PositiveIdMethod;
    evidence: string | null;
}

export interface PositiveIdVerifyResponse {
    verifiedToken: string;
    expiresInSeconds: number;
}

/** Header the protected action call must carry so [RequirePositiveId] accepts it. */
export const POSITIVE_ID_TOKEN_HEADER = 'X-PositiveId-Token';

export interface PositiveIdCredential {
    id: number;
    userName: string;
    method: PositiveIdMethod;
    enrolledUtc: string;
    enrolledBy: string;
}

/** Status of a user's biometric consent under the tenant's current biometric privacy policy. */
export interface BiometricConsentStatus {
    userName: string;
    /** SHA-256 hash of the currently-configured disclosure document; empty if not configured. */
    currentPolicyVersion: string;
    /** Image ID of the currently-configured disclosure document; null if not configured. */
    currentDocumentId: number | null;
    /** True if the tenant has a disclosure document configured. */
    policyConfigured: boolean;
    /** True if the user has an active (non-revoked) consent for the current policy version. */
    hasActiveConsent: boolean;
}

/** Confirmation payload for a recorded biometric consent. */
export interface BiometricConsentRecord {
    id: number;
    userName: string;
    consentedUtc: string;
    policyVersion: string;
    documentId: number;
    recordedBy: string;
}

/** Filter inputs for the positive-identification events inspection report. */
export interface PositiveIdEventSearchFilters {
    userName?: string | null;
    actionType?: PositiveIdActionType | null;
    targetEntityType?: PositiveIdTargetEntityType | null;
    targetEntityId?: string | null;
    method?: PositiveIdMethod | null;
    fromUtc?: string | null;
    toUtc?: string | null;
    offset?: number;
    limit?: number;
}

/** A row of the positive-identification events report. */
export interface PositiveIdEventRow {
    id: number;
    userName: string;
    actionType: PositiveIdActionType;
    targetEntityType: PositiveIdTargetEntityType;
    targetEntityId: string;
    method: PositiveIdMethod;
    evidenceRef: string | null;
    challengeIssuedUtc: string;
    actionPerformedUtc: string;
    correlationId: string;
    totalCount: number;
}

/** Cached snapshot of the tenant's positive-identification configuration. */
export interface PositiveIdConfig {
    enabled: boolean;
    methods: string[] | null;
}

// In-flight promise so concurrent callers share one GET; cleared on settle so each mount re-fetches
// and a Configuration toggle takes effect on navigation without a full reload.
let configPromise: Promise<PositiveIdConfig> | null = null;

class PositiveIdService {
    /** Resolves the positive-id config (enabled flag + methods). Deduped while in-flight, re-fetched each call. */
    static getConfig(): Promise<PositiveIdConfig> {
        if (configPromise) return configPromise;
        configPromise = (async () => {
            try {
                const resp = await axios.post<Record<string, any>>(
                    '/Configuration/values',
                    ['EnablePositiveIdentification', 'PositiveIdMethods'],
                );
                const enabled = resp.data?.EnablePositiveIdentification ?? false;
                const methodsRaw = resp.data?.PositiveIdMethods;
                let methods: string[] | null = null;
                if (Array.isArray(methodsRaw)) {
                    methods = methodsRaw;
                } else if (typeof methodsRaw === 'string' && methodsRaw) {
                    try {
                        const parsed = JSON.parse(methodsRaw);
                        methods = Array.isArray(parsed) ? parsed : null;
                    } catch {
                        methods = null;
                    }
                }
                return { enabled, methods };
            } catch {
                return { enabled: false, methods: null };
            } finally {
                configPromise = null;
            }
        })();
        return configPromise;
    }

    static async challenge(request: PositiveIdChallengeRequest): Promise<PositiveIdChallengeResponse> {
        const response = await axios.post<PositiveIdChallengeResponse>('/api/positiveid/challenge', request);
        return response.data;
    }

    static async verify(request: PositiveIdVerifyRequest): Promise<PositiveIdVerifyResponse> {
        const response = await axios.post<PositiveIdVerifyResponse>('/api/positiveid/verify', request);
        return response.data;
    }

    /** Records the operator's positive-ID for a filling-station session. Requires a verified token. */
    static async acknowledgeFilling(verifiedToken: string, userName: string): Promise<void> {
        await axios.post('/api/positiveid/filling/acknowledge', { userName },
            { headers: { [POSITIVE_ID_TOKEN_HEADER]: verifiedToken } });
    }

    /** Admin: list a user's active enrolled credentials. */
    static async listCredentials(userName: string): Promise<PositiveIdCredential[]> {
        const response = await axios.get<PositiveIdCredential[]>(`/api/positiveid/enrollment/${encodeURIComponent(userName)}`);
        return response.data;
    }

    /** Admin: enroll a badge credential for a user. */
    static async enrollBadge(userName: string, badgeEvidence: string): Promise<PositiveIdCredential> {
        const response = await axios.post<PositiveIdCredential>('/api/positiveid/enrollment/badge', { userName, badgeEvidence });
        return response.data;
    }

    /**
     * Admin: enroll a biometric (fingerprint) credential for a user.
     *
     * <p><code>templates</code> is an ordered list of base64-encoded ISO/IEC 19794-2 FMDs — one per
     * directional presentation (center, left, up, down, right). The server consolidates them into a
     * single high-density enrollment FMD via the DigitalPersona engine, encrypts that with KMS, and
     * stores it. Raw fingerprint images are never transmitted.</p>
     */
    static async enrollBiometric(userName: string, templates: string[]): Promise<PositiveIdCredential> {
        const response = await axios.post<PositiveIdCredential>('/api/positiveid/enrollment/biometric', { userName, templates });
        return response.data;
    }

    /** Admin: revoke an enrolled credential. */
    static async revokeCredential(id: number): Promise<void> {
        await axios.post(`/api/positiveid/enrollment/${id}/revoke`);
    }

    /** Returns the user's biometric consent status under the tenant's current policy version. */
    static async getBiometricConsentStatus(userName: string): Promise<BiometricConsentStatus> {
        const response = await axios.get<BiometricConsentStatus>(
            `/api/positiveid/enrollment/consent/${encodeURIComponent(userName)}`,
        );
        return response.data;
    }

    /**
     * Records the subject's informed consent for the tenant's current biometric privacy policy
     * version. Required before any fingerprint enrollment attempt — the backend refuses biometric
     * enrollment unless an active consent record exists.
     */
    static async recordBiometricConsent(userName: string, policyVersion: string): Promise<BiometricConsentRecord> {
        const response = await axios.post<BiometricConsentRecord>(
            '/api/positiveid/enrollment/consent',
            { userName, policyVersion },
        );
        return response.data;
    }

    /**
     * Fetches the consent disclosure PDF bytes for inline rendering in the consent dialog.
     * Uses the standard CPHub <c>/api/Image/{id}</c> retrieval endpoint (the same one that serves the
     * Patient Rights PDF), returned as a Blob so the caller can create an object URL for an iframe.
     */
    static async getBiometricConsentDocumentBlob(documentId: number): Promise<Blob> {
        const response = await axios.get(`/api/Image/${documentId}`, { responseType: 'blob' });
        return response.data as Blob;
    }

    /**
     * Paginated search of positive-identification events, for the board-inspection report.
     * Requires the <c>Compliance/Positive ID Inspection</c> permission.
     */
    static async searchEvents(filters: PositiveIdEventSearchFilters, signal?: AbortSignal):
        Promise<{ totalCount: number; rows: PositiveIdEventRow[] }> {
        const response = await axios.get<{ totalCount: number; rows: PositiveIdEventRow[] }>(
            '/api/positiveid/events',
            { params: filters, signal },
        );
        return response.data;
    }
}

export default PositiveIdService;
